import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Email as EmailIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import { overlaySurface } from "../Dashboard/overviewPanel";

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Support contact dialog — email plus the company and personal X accounts.
 * Shared by the web profile menu (UserProfileButton) and the native More
 * sheet so both surfaces present identical help content.
 */
const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: overlaySurface(theme.palette.mode === "dark"),
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Need Help?
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        <Typography
          sx={{
            pt: 2,
            fontSize: '0.9375rem',
            color: theme.palette.text.secondary,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          We're here to help! Reach out to us for feedback, assistance with the
          application, or anything you may need.
        </Typography>

        {/* Support Email */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2.5,
            p: 2,
            borderRadius: 1.5,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <EmailIcon
            sx={{
              fontSize: 20,
              color: theme.palette.primary.main,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.25,
              }}
            >
              Email Support
            </Typography>
            <Link
              href="mailto:support@insightanalytics.io"
              underline="none"
              sx={{
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: theme.palette.primary.main,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              support@insightanalytics.io
            </Link>
          </Box>
        </Box>

        {/* Company X */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2.5,
            p: 2,
            borderRadius: 1.5,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <XIcon
            sx={{
              fontSize: 20,
              color: theme.palette.primary.main,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.25,
              }}
            >
              Company X (Twitter)
            </Typography>
            <Link
              href="https://x.com/itsinsightio"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: theme.palette.primary.main,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              @itsinsightio
            </Link>
          </Box>
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default HelpModal;
